"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Plus, Volume2, VolumeX } from "lucide-react";
import { motion } from "motion/react";
import TextGenerateEffect from "@/components/TextGenerateEffect";
import TopGradient from "@/components/TopGradient";
import BOOKS_DATA from "@/app/books/data";

// Shared motion spec — Apple-style long-tail ease for the hover reveal
// on the portrait so the blur + copy fade in feels produced rather than
// snappy.
const PORTRAIT_EASE = [0.22, 1, 0.36, 1];

// Card entrance — soft rise-and-sharpen. Used on every scroll-into-view
// group of cards (stats, books, beyond) with a stagger container so the
// cards cascade in rather than pop together.
const CARD_REVEAL = {
  hidden: { opacity: 0, y: 32, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.85, ease: PORTRAIT_EASE },
  },
};

const CARD_GRID = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const VIEWPORT_ONCE = { once: true, amount: 0.2 };

const EXPERTISE_TOPICS = [
  "Spine health, management, and recovery",
  "Minimally invasive spine surgery and surgical craft",
  "Innovation in medical technology — robotics, navigation, and AR",
];

// Each achievement row is rendered as a 2-column editorial pair:
// uppercase kicker on the left, big title + note on the right. Count
// is baked into the title copy rather than a separate column.
const RECOGNITION = [
  {
    kicker: "LA Super Doctor",
    title: "14 consecutive years recognized.",
    note: "Los Angeles Magazine peer-nominated top-physician honor, 14 years running.",
  },
  {
    kicker: "OC Physician of Excellence",
    title: "7 years honored in a row.",
    note: "Orange County Medical Association's peer-voted recognition of physicians of excellence.",
  },
  {
    kicker: "Scientific presentations",
    title: "151 invited talks worldwide.",
    note: "Delivered across international spine, neurosurgery, and innovation conferences.",
  },
  {
    kicker: "Peer-reviewed publications",
    title: "34 research papers.",
    note: "Published in leading medical journals, advancing minimally invasive spine care.",
  },
  {
    kicker: "Clinical textbooks",
    title: "10 book chapters contributed.",
    note: "Across clinical, surgical, and anatomy-focused volumes on spine.",
  },
  {
    kicker: "Published books",
    title: "3 full-length titles.",
    note: "Including the Minimally Invasive Spine Surgery textbook, plus memoir and family-fiction titles.",
  },
];

// Pull book covers + Amazon links from the shared data file so the
// "Published books" block matches /books and the homepage sticky cards.
const BOOK_NOTES = {
  "minimally-invasive-spine-surgery":
    "Authoritative textbook on spine surgery technique",
  "grabbing-life-by-the-horns":
    "A compilation of fascinating patient stories and lessons learned",
  "pugslys-idea-goes-viral":
    "A family-style pet/human take on the recent pandemic",
};

const BOOKS = [
  "minimally-invasive-spine-surgery",
  "grabbing-life-by-the-horns",
  "pugslys-idea-goes-viral",
].map((slug) => {
  const src = BOOKS_DATA.find((b) => b.slug === slug) || {};
  return {
    slug,
    title: src.title || slug,
    image: src.image,
    note: BOOK_NOTES[slug] || src.subtitle,
    buyUrl: src.paperback || src.kindle,
  };
});

const BEYOND = [
  {
    title: "Mission-driven surgeon",
    body: "Led multiple teams on medical and surgical mission trips — providing care to refugees and the underserved.",
  },
  {
    title: "CATAN champion",
    body: "2025 US National CATAN Champion — representing the USA at the 2026 Americas Tournament in Brazil and the 2027 World Tournament.",
  },
  {
    title: "Endurance athlete",
    body: "Completed 27 triathlons. Avid pickleball player — recent tournament winner in Newport Beach, CA.",
  },
  {
    title: "Film — Sundance 2018",
    body: "Executive producer of “The Kindergarten Teacher,” featured at the Sundance Film Festival in 2018.",
  },
  {
    title: "Original screenplay",
    body: "Completed an original feature-length script, “Killing Me Softly.”",
  },
  {
    title: "“The Cutting Edge”",
    body: "Developing a unique surgeon/physician-based reality game-show concept — Top Chef meets Big Brother.",
  },
  {
    title: "Survivor application",
    body: "Actively applying to be on the next season of Survivor.",
  },
  {
    title: "Deep-sea fisherman",
    body: "Caught a 350 lb Blue Marlin and a 250 lb Bluefin Tuna — and makes his own sushi / sashimi.",
  },
];

export default function AboutPage() {
  const videoIframeRef = useRef(null);
  const videoWrapRef = useRef(null);
  const videoSectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  // Mute/unmute + force-play. On-load sync is deferred 800ms so
  // YouTube's internal message handler has time to initialize.
  useEffect(() => {
    const iframe = videoIframeRef.current;
    if (!iframe) return;

    const sendCommand = (func) => {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    };

    const syncState = () => {
      sendCommand(muted ? "mute" : "unMute");
      sendCommand("playVideo");
    };

    syncState();
    const onLoad = () => setTimeout(syncState, 800);
    iframe.addEventListener("load", onLoad);
    return () => iframe.removeEventListener("load", onLoad);
  }, [muted]);

  useEffect(() => {
    let fired = false;
    const kick = () => {
      if (fired) return;
      fired = true;
      const iframe = videoIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: [] }),
          "*"
        );
      }
    };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("click", kick, opts);
    window.addEventListener("scroll", kick, opts);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("click", kick);
      window.removeEventListener("scroll", kick);
    };
  }, []);

  // GSAP reveal — the video frame opens from a narrow, slightly blurred
  // rectangle to full size as the user scrolls into the section. Scrubbed
  // so it feels like the video is "pushing" into view rather than popping.
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const wrap = videoWrapRef.current;
    const section = videoSectionRef.current;
    if (!wrap || !section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        wrap,
        {
          clipPath: "inset(20% 28% 20% 28% round 28px)",
          filter: "blur(14px)",
          scale: 0.94,
          opacity: 0.3,
        },
        {
          clipPath: "inset(0% 0% 0% 0% round 24px)",
          filter: "blur(0px)",
          scale: 1,
          opacity: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 25%",
            scrub: 1.1,
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <main className="ab-page">
      <TopGradient />
      {/* ───────── Hero ───────── */}
      <section className="ab-hero" data-bg-tint="neutral">
        <div className="ab-hero-kicker">
          <Plus size={16} aria-hidden />
          <span>About</span>
        </div>
        <TextGenerateEffect
          as="h1"
          words="Burak Ozgur MD"
          className="ab-hero-name"
          duration={0.8}
          staggerDelay={0.05}
          filter={false}
        />
        <TextGenerateEffect
          as="p"
          words="World-Renowned Neurosurgeon &middot; Innovator &middot; Modern-day Renaissance Man"
          className="ab-hero-titles"
          duration={0.7}
          staggerDelay={0.03}
          filter={false}
        />
      </section>

      {/* ───────── Intro — fabrica-style headline row + content row ───────── */}
      <section className="ab-intro" data-bg-tint="warm">
        {/* Row 1: kicker left, big 2-tone headline right */}
        <div className="ab-intro-head">
          <div className="ab-col-kicker">
            <Plus size={16} aria-hidden />
            <span>Introduction</span>
          </div>
          <TextGenerateEffect
            as="h2"
            words="Two decades pioneering spine surgery, **with a focus on innovation and patient outcomes.**"
            className="ab-intro-headline"
            duration={0.9}
            staggerDelay={0.04}
            filter={false}
            highlightColor="#6e6e73"
          />
        </div>

        {/* Row 2: narrow portrait left, paragraph + 2×2 stat grid right */}
        <div className="ab-intro-content">
          {/* Portrait — motion hover group: photo blurs, dim layer fades
              in, CTA overlay (copy + pill) slides up from the bottom. */}
          <motion.div
            className="ab-intro-photo"
            initial="rest"
            animate="rest"
            whileHover="hover"
          >
            <motion.img
              src="https://for-media.b-cdn.net/ozgurmd/burak-ozgur-headshot-2.JPG"
              alt="Dr. Burak M. Ozgur"
              variants={{
                rest: { filter: "blur(0px)", scale: 1 },
                hover: { filter: "blur(6px)", scale: 1.04 },
              }}
              transition={{ duration: 0.55, ease: PORTRAIT_EASE }}
            />
            <motion.div
              className="ab-intro-photo-dim"
              variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
              transition={{ duration: 0.45, ease: PORTRAIT_EASE }}
              aria-hidden
            />
            <motion.div
              className="ab-intro-photo-overlay"
              variants={{
                rest: { opacity: 0, y: 14 },
                hover: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.5, ease: PORTRAIT_EASE }}
            >
              <p className="ab-intro-photo-copy">
                Your journey begins with a conversation — reach out directly.
              </p>
              <button
                type="button"
                className="ab-intro-photo-cta"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("bo:open-contact"))
                }
              >
                <span>Get in Touch with Burak</span>
                <span className="ab-intro-photo-dot" aria-hidden />
              </button>
            </motion.div>
          </motion.div>

          <div className="ab-intro-body">
            <TextGenerateEffect
              as="p"
              words="Dr. Ozgur combines **authority, innovation, and relatability.**
            He is admired for his ability to translate advanced medical knowledge into practical, actionable guidance."
              className="ab-intro-copy"
              duration={0.8}
              staggerDelay={0.02}
              filter={false}
              highlightColor="#1d1d1f"
            />

            {/* 2×2 split stat grid — each of the 4 cards has its own
                background, mirroring the fabrica layout where the big
                number and the label live in separate cards. */}
            <motion.div
              className="ab-stats-grid"
              variants={CARD_GRID}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_ONCE}
            >
              <motion.article
                variants={CARD_REVEAL}
                className="ab-stat-card ab-stat-card--value"
              >
                <div className="ab-stat-value">20+</div>
                <div className="ab-stat-ordinal">01</div>
              </motion.article>
              <motion.article
                variants={CARD_REVEAL}
                className="ab-stat-card ab-stat-card--value"
              >
                <div className="ab-stat-value">151</div>
                <div className="ab-stat-ordinal">02</div>
              </motion.article>
              <motion.article
                variants={CARD_REVEAL}
                className="ab-stat-card ab-stat-card--note"
              >
                <div className="ab-stat-label">Years in practice</div>
                <div className="ab-stat-note">
                  Over two decades of dual-board-certified neurosurgical and
                  spine-surgery practice.
                </div>
              </motion.article>
              <motion.article
                variants={CARD_REVEAL}
                className="ab-stat-card ab-stat-card--note"
              >
                <div className="ab-stat-label">Global presentations</div>
                <div className="ab-stat-note">
                  Invited talks and scientific presentations delivered across
                  conferences worldwide.
                </div>
              </motion.article>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── Video reveal ───────── */}
      <section
        ref={videoSectionRef}
        id="ab-video"
        className="ab-video-section"
        data-bg-tint="cool"
      >
        <div ref={videoWrapRef} className="ab-video-frame">
          <iframe
            ref={videoIframeRef}
            src={`https://www.youtube.com/embed/l5WYzL04s5g?autoplay=1&mute=1&controls=0&modestbranding=1&loop=1&playlist=l5WYzL04s5g&playsinline=1&enablejsapi=1&rel=0${origin ? `&origin=${encodeURIComponent(origin)}` : ""}`}
            title="Burak Ozgur in action"
            className="ab-video-iframe"
            allow="autoplay; encrypted-media; picture-in-picture"
          />
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="ab-video-mute"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>
      </section>

      {/* ───────── Media-ready expertise ───────── */}
      <section className="ab-two-col" data-bg-tint="peach">
        <div className="ab-col-kicker">
          <Plus size={16} aria-hidden />
          <span>Media-ready expertise</span>
        </div>
        <div className="ab-col-body">
          <TextGenerateEffect
            as="p"
            words="Dr. Ozgur's influence reaches beyond the operating room. He is a **compelling speaker and on-camera expert** who makes complex medical topics **accessible to wide audiences**."
            className="ab-big-copy"
            duration={0.8}
            staggerDelay={0.02}
            filter={false}
            highlightColor="#1d1d1f"
          />
          <div className="ab-topics-block">
            <div className="ab-topics-label">
              <Plus size={16} aria-hidden />
              <span>His insights span</span>
            </div>
            <ul className="ab-topics">
              {EXPERTISE_TOPICS.map((t, i) => (
                <li key={i}>
                  <TextGenerateEffect
                    as="span"
                    words={t}
                    duration={0.6}
                    staggerDelay={0.02}
                    filter={false}
                    highlightColor="#1d1d1f"
                  />
                </li>
              ))}
            </ul>
          </div>
          <TextGenerateEffect
            as="p"
            words="With an **approachable style**, he bridges the gap between medical science and everyday health — making him an **ideal guest for television, podcasts, and live events**."
            className="ab-big-copy"
            duration={0.8}
            staggerDelay={0.02}
            filter={false}
            highlightColor="#1d1d1f"
          />
        </div>
      </section>

      {/* ───────── Thought leadership — achievements table ───────── */}
      <section id="ab-awards" className="ab-awards" data-bg-tint="lilac">
        <div className="ab-col-kicker">
          <Plus size={16} aria-hidden />
          <span>Thought leadership</span>
        </div>
        <div className="ab-awards-content">
          <TextGenerateEffect
            as="h2"
            words="Achievements."
            className="ab-awards-title"
            duration={0.9}
            staggerDelay={0.07}
            filter={false}
          />
          <div className="ab-awards-sub">(2005–26&copy;)</div>

          <motion.div
            className="ab-awards-table"
            variants={CARD_GRID}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
          >
            {RECOGNITION.map((row, i) => (
              <motion.div
                key={i}
                variants={CARD_REVEAL}
                className="ab-awards-row"
              >
                <div className="ab-awards-kicker">{row.kicker}</div>
                <div className="ab-awards-title-col">
                  <h3 className="ab-awards-row-title">{row.title}</h3>
                  <p className="ab-awards-row-note">{row.note}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="ab-books">
            <motion.ul
              variants={CARD_GRID}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_ONCE}
            >
              {BOOKS.map((b) => (
                <motion.li
                  key={b.slug}
                  variants={CARD_REVEAL}
                  className="ab-book-row"
                >
                  <div className="ab-book-cover">
                    {b.image ? <img src={b.image} alt={b.title} /> : null}
                  </div>
                  <div className="ab-book-text">
                    <div className="ab-books-title">{b.title}</div>
                    <div className="ab-books-note">{b.note}</div>
                  </div>
                  {b.buyUrl ? (
                    <a
                      href={b.buyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ab-book-buy"
                    >
                      <span>Buy on Amazon</span>
                    </a>
                  ) : null}
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* ───────── Why he stands out → Renaissance Man (single dock zone) ───────── */}
      <div id="ab-spans" className="ab-spans-group">
      {/* ───────── Why he stands out ───────── */}
      <section className="ab-two-col" data-bg-tint="cool">
        <div className="ab-col-kicker">
          <Plus size={16} aria-hidden />
          <span>Why he stands out</span>
        </div>
        <div className="ab-col-body">
          <TextGenerateEffect
            as="p"
            words="Dr. Ozgur combines **authority, innovation, and relatability**. He is admired for his ability to translate advanced medical knowledge into practical, actionable guidance. His trusted reputation and unique communication style make him a **powerful voice at the intersection of health, science, and wellness**."
            className="ab-big-copy"
            duration={0.8}
            staggerDelay={0.02}
            filter={false}
            highlightColor="#1d1d1f"
          />
        </div>
      </section>

      {/* ───────── Renaissance Man grid ───────── */}
      <section className="ab-beyond" data-bg-tint="mint">
        <div className="ab-col-kicker">
          <Plus size={16} aria-hidden />
          <span>Beyond the OR</span>
        </div>
        <div className="ab-beyond-content">
          <TextGenerateEffect
            as="h2"
            words="Renaissance Man."
            className="ab-awards-title"
            duration={0.9}
            staggerDelay={0.06}
            filter={false}
          />
          <motion.div
            className="ab-beyond-grid"
            variants={CARD_GRID}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
          >
            {BEYOND.map((item, i) => (
              <motion.article
                key={i}
                variants={CARD_REVEAL}
                className="ab-beyond-card"
              >
                <div className="ab-beyond-num">
                  ({String(i + 1).padStart(2, "0")})
                </div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>
      </div>

      {/* For-inquiries CTA lives in the Footer now (see Footer.jsx). */}
    </main>
  );
}
